import api, { v2BaseUrl } from "../api";
import { DashboardFilter } from "../../types/dashboard";
import { buildAssetTreePayload } from "@utils/assetTreePayload";
import {
  buildAlertDetail,
  MAINTENANCE_ALERT_HISTORY_MOCK,
  MAINTENANCE_ALERT_TOTALS_MOCK,
  MAINTENANCE_SUBCATEGORIES_MOCK,
  MAINTENANCE_TRANSACTIONS_MOCK,
  MAINTENANCE_VEHICLES_MOCK,
  MaintenanceAlert,
  MaintenanceAlertDetail,
  MaintenanceAlertTotals,
  MaintenanceAlertType,
  MaintenanceSubcategoryTotal,
  MaintenanceSummary,
  MaintenanceSummaryMetric,
  MaintenanceTransaction,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "./mock";

export { MAINTENANCE_ALERT_TYPES } from "./mock";

export type {
  MaintenanceAlert,
  MaintenanceAlertDetail,
  MaintenanceAlertMetric,
  MaintenanceAlertSeverity,
  MaintenanceAlertTotals,
  MaintenanceAlertType,
  MaintenanceStatus,
  MaintenanceSubcategoryTotal,
  MaintenanceSummary,
  MaintenanceSummaryMetric,
  MaintenanceTransaction,
  MaintenanceType,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "./mock";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_TOP_LIMIT = 5;

export interface MaintenancePage<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const paginate = <T>(items: T[], page: number, size: number): MaintenancePage<T> => ({
  content: items.slice(page * size, page * size + size),
  page,
  size,
  totalElements: items.length,
  totalPages: Math.max(1, Math.ceil(items.length / size)),
});

const EMPTY_METRIC: MaintenanceSummaryMetric = {
  value: 0,
  previousValue: 0,
  variationPercent: null,
  currentPeriod: null,
  previousPeriod: null,
};

export const EMPTY_MAINTENANCE_SUMMARY: MaintenanceSummary = {
  totalSpent: EMPTY_METRIC,
  averageCostPerKm: { ...EMPTY_METRIC, kmDriven: 0 },
  averageCostPerVehicle: EMPTY_METRIC,
};

const toNullableNumber = (value: unknown): number | null => (value == null ? null : Number(value));

const normalizeMetric = (metric: any): MaintenanceSummaryMetric => ({
  value: Number(metric?.value ?? 0),
  previousValue: Number(metric?.previousValue ?? 0),
  variationPercent: toNullableNumber(metric?.variationPercent),
  currentPeriod: metric?.currentPeriod ?? null,
  previousPeriod: metric?.previousPeriod ?? null,
});

const buildPeriodFilterBody = (filters: DashboardFilter) => ({
  startDate: filters.startDate,
  endDate: filters.endDate,
  assetTree: buildAssetTreePayload(filters),
});

export const getMaintenanceSummary = async (filters: DashboardFilter): Promise<MaintenanceSummary> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/summary`, buildPeriodFilterBody(filters));
    const data = response.data?.data ?? {};

    return {
      totalSpent: normalizeMetric(data.totalSpent),
      averageCostPerKm: {
        ...normalizeMetric(data.averageCostPerKm),
        kmDriven: Number(data.averageCostPerKm?.kmDriven ?? 0),
      },
      averageCostPerVehicle: normalizeMetric(data.averageCostPerVehicle),
    };
  } catch (error) {
    console.error("Error fetching maintenance summary:", error);
    return EMPTY_MAINTENANCE_SUMMARY;
  }
};

export const getMaintenanceByType = async (filters: DashboardFilter): Promise<MaintenanceTypeTotal[]> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/by-type`, buildPeriodFilterBody(filters));
    const items = response.data?.data;

    if (!Array.isArray(items)) return [];

    return items.map((item) => ({
      type: item?.type,
      amount: Number(item?.amount ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching maintenance totals by type:", error);
    return [];
  }
};

export const getTopMaintenanceSubcategories = async (
  _filters: DashboardFilter,
  limit = DEFAULT_TOP_LIMIT
): Promise<MaintenanceSubcategoryTotal[]> => MAINTENANCE_SUBCATEGORIES_MOCK.slice(0, limit);

export const getTopMaintenanceVehicles = async (
  _filters: DashboardFilter,
  limit = DEFAULT_TOP_LIMIT
): Promise<MaintenanceVehicleCost[]> => MAINTENANCE_VEHICLES_MOCK.slice(0, limit);

export const getMaintenanceTransactions = async (
  _filters: DashboardFilter,
  page = 0,
  size = DEFAULT_PAGE_SIZE
): Promise<MaintenancePage<MaintenanceTransaction>> => paginate(MAINTENANCE_TRANSACTIONS_MOCK, page, size);

export const getMaintenanceTransactionDetail = async (id: string): Promise<MaintenanceTransaction | null> =>
  MAINTENANCE_TRANSACTIONS_MOCK.find((transaction) => transaction.id === id) ?? null;

export const getMaintenanceAlertTotals = async (_filters: DashboardFilter): Promise<MaintenanceAlertTotals> => ({
  total: MAINTENANCE_ALERT_TOTALS_MOCK.total,
  alerts: { ...MAINTENANCE_ALERT_TOTALS_MOCK.alerts },
});

export const getMaintenanceAlertHistory = async (
  type?: MaintenanceAlertType,
  page = 0,
  size = DEFAULT_PAGE_SIZE
): Promise<MaintenancePage<MaintenanceAlert>> => {
  const items = type
    ? MAINTENANCE_ALERT_HISTORY_MOCK.filter((alert) => alert.type === type)
    : MAINTENANCE_ALERT_HISTORY_MOCK;
  return paginate(items, page, size);
};

export const getMaintenanceAlertDetail = async (alertId: string): Promise<MaintenanceAlertDetail | null> => {
  const alert = MAINTENANCE_ALERT_HISTORY_MOCK.find((item) => item.id === alertId);
  return alert ? buildAlertDetail(alert) : null;
};

export const exportMaintenance = async (_filters: DashboardFilter): Promise<void> => undefined;
